import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildCertificateFromInvoice, formatIdTypeOperation } from '@erp/domain/tax';
import type { WithholdingCertificateType } from '@erp/data-access/interfaces';
import { useCreateCertificate } from '@/hooks/use-withholding';
import { useCompanySettings } from '@/hooks/use-settings';
import { useDataService } from '@/lib/service-context';

export interface CreateCertificateSource {
  id: string;
  number: string;
  docDate: string;
  totalHt: number;
  totalVat: number;
  totalTtc: number;
}

export interface CreateCertificateParty {
  id: string;
  type: 'company' | 'individual';
}

/**
 * Crée une retenue à la source à partir d'UNE OU PLUSIEURS factures d'un même
 * tiers et redirige vers sa fiche. Les factures doivent toutes appartenir au
 * même tiers — la contrainte est vérifiée au niveau de l'appelant avant de
 * déléguer à ce hook.
 *
 * Cas 1 facture → 1 retenue avec 1 ligne (comportement historique préservé).
 * Cas N factures → 1 retenue avec N lignes (une par facture).
 */
export function useCreateCertificateFromDocument() {
  const navigate = useNavigate();
  const createMut = useCreateCertificate();
  const { data: settings } = useCompanySettings();
  const ds = useDataService();
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const createCertificate = async (
    certificateType: WithholdingCertificateType,
    sources: CreateCertificateSource | CreateCertificateSource[],
    party: CreateCertificateParty,
  ) => {
    const currentSettings = settingsRef.current;
    if (!currentSettings) return null;

    const sourceList: CreateCertificateSource[] = Array.isArray(sources) ? sources : [sources];
    if (sourceList.length === 0) return null;

    // Reuse an existing certificate instead of creating a duplicate for any source.
    for (const source of sourceList) {
      const existing = await ds.withholding.getCertificateBySourceId(source.id);
      if (existing) {
        navigate(`/withholding/${certificateType === 'sale' ? 'sales' : 'purchases'}/${existing.id}`);
        return existing;
      }
    }

    const seqStart = await ds.withholding.getNextOperationSequence();

    // Pour chaque facture, calcule sa ligne RAS puis agrège
    let totalHt = 0;
    let totalVat = 0;
    let totalTtc = 0;
    let totalRas = 0;
    let totalNetServi = 0;
    const lines: Array<{
      idTypeOperation: string;
      operationTypeId: null;
      anneeFacturation: number;
      cnpc: number;
      pCharge: number;
      montantHt: number;
      tauxRs: number;
      tauxTva: number;
      montantTva: number;
      montantTtc: number;
      montantRs: number;
      montantNetServi: number;
    }> = [];

    for (let i = 0; i < sourceList.length; i++) {
      const source = sourceList[i]!;
      const avgVatRate = source.totalHt > 0
        ? Math.round((source.totalVat / source.totalHt) * 100)
        : 0;
      const draft = buildCertificateFromInvoice(
        {
          docDate: source.docDate,
          totalHt: source.totalHt,
          totalVat: source.totalVat,
          totalTtc: source.totalTtc,
          vatRate: avgVatRate,
        },
        { type: party.type },
        {
          withholdingRate: currentSettings.withholdingRate ?? 1,
          withholdingThreshold: currentSettings.withholdingThreshold ?? 1000,
        },
        1,
        1,
      );
      if (draft.totalRas <= 0) return null;
      totalHt += draft.totalHt;
      totalVat += draft.totalVat;
      totalTtc += draft.totalTtc;
      totalRas += draft.totalRas;
      totalNetServi += draft.totalNetServi;
      draft.lines.forEach((l, idx) => {
        lines.push({
          ...l,
          idTypeOperation: formatIdTypeOperation(1, seqStart + lines.length + idx),
          operationTypeId: null,
        });
      });
    }

    if (totalRas <= 0) return null;

    const refNumbers = sourceList.map((s) => s.number).join(', ');
    const firstDocDate = sourceList[0]!.docDate;

    const certificate = await createMut.mutateAsync({
      certificateType,
      thirdPartyType: certificateType === 'sale' ? 'client' : 'supplier',
      thirdPartyId: party.id,
      sourceDocumentIds: sourceList.map((s) => s.id),
      datePayment: new Date().toISOString().slice(0, 10),
      refCertif: `${new Date().getFullYear()}/${refNumbers}`,
      totalHt,
      totalVat,
      totalTtc,
      totalRas,
      totalNetServi,
      notes: sourceList.length > 1
        ? `Retenue groupée sur ${sourceList.length} factures (${refNumbers}), datées à partir du ${firstDocDate}.`
        : undefined,
      lines,
    });

    navigate(`/withholding/${certificateType === 'sale' ? 'sales' : 'purchases'}/${certificate.id}`);
    return certificate;
  };

  const ConfirmationDialog = () => null;

  return { createCertificate, ConfirmationDialog };
}
